/**
 * AnimationStateMachine: drives the avatar's Idle/Walk/Run/Jump clips.
 *
 * Each frame the third-person controller passes {speed, grounded, jumping,
 * running}; this picks a target state and cross-fades to it via the avatar's
 * anim layer. Missing clips fall back to idle (warned once). When the avatar
 * has no anim component (placeholder capsule), update is a no-op.
 *
 * States: idle | walk | run | jump. Jump is one-shot (not looped); movement
 * input cannot interrupt it while airborne, but landing may exit it early.
 */

import type { AvatarController } from './AvatarController';
import type { AnimInputs } from './ThirdPersonController';

type AnimState = 'idle' | 'walk' | 'run' | 'jump';

/** Cross-fade durations per transition class (seconds). */
const FADE_LOCO = 0.18;
const FADE_JUMP = 0.08;

class AnimationStateMachine {
    private _avatar: AvatarController;
    private _current: AnimState | null = null;
    private _manualAction: string | null = null;
    private _warned = new Set<AnimState>();
    private _airborneTime = 0;
    private readonly _airborneGrace = 0.12;
    private readonly _jumpExitProgress = 0.98;

    constructor(avatar: AvatarController) {
        this._avatar = avatar;
    }

    update(dt: number, inputs: AnimInputs): void {
        this._airborneTime = inputs.grounded ? 0 : this._airborneTime + dt;
        if (this._manualAction) this._updateManualAction(inputs);
        if (this._manualAction) return;
        if (this._current === 'jump' && !inputs.grounded && !this._jumpFinished()) return;

        const target = this._decide(inputs);
        if (target === this._current) return;

        // Fall back to idle if the target clip is missing.
        let state: AnimState | null = target;
        if (!this._avatar.hasState(state)) {
            if (!this._warned.has(target)) {
                this._warned.add(target);
                console.warn(`[AnimationStateMachine] 动画状态 "${target}" 不可用，回退 idle`);
            }
            state = this._avatar.hasState('idle') ? 'idle' : null;
        }
        if (!state) return;

        const layer = this._avatar.animLayer;
        if (!layer) return;

        const fade = target === 'jump' ? FADE_JUMP : FADE_LOCO;
        try {
            layer.transition(state, fade);
            this._current = target;
        } catch (e) {
            console.warn('[AnimationStateMachine] transition 失败：', e);
        }
    }

    private _decide(inputs: AnimInputs): AnimState {
        // Brief ground-probe/capsule-contact flicker should not flash the jump clip.
        if (!inputs.grounded && (inputs.jumping || this._airborneTime >= this._airborneGrace)) return 'jump';
        if (inputs.speed < 0.3) return 'idle';
        return inputs.running ? 'run' : 'walk';
    }

    private _jumpFinished(): boolean {
        const progress = this._avatar.animLayer?.activeStateProgress;
        return typeof progress === 'number' && progress >= this._jumpExitProgress;
    }

    get manualActionActive(): boolean {
        return this._manualAction !== null;
    }

    /** The name of the currently playing manual action (e.g. 'Clap'), or null. */
    get manualActionName(): string | null {
        return this._manualAction;
    }

    playAction(name: string): boolean {
        if (!this._avatar.hasState(name)) return false;
        const layer = this._avatar.animLayer;
        if (!layer) return false;
        try {
            layer.transition(name, FADE_LOCO);
            this._manualAction = name;
            this._current = null;
            return true;
        } catch (e) {
            console.warn(`[AnimationStateMachine] action transition failed: ${name}`, e);
            return false;
        }
    }

    private _updateManualAction(inputs: AnimInputs): void {
        if (!this._manualAction) return;
        if (inputs.speed >= 0.3 || !inputs.grounded || inputs.jumping) {
            this._manualAction = null;
            return;
        }
        const progress = this._avatar.animLayer?.activeStateProgress;
        if (typeof progress === 'number' && progress >= 0.98) {
            this._manualAction = null;
            this.reset();
        }
    }

    /** Reset to idle (e.g. on mode switch / spawn). */
    reset(): void {
        this._current = null;
        this._manualAction = null;
        this._airborneTime = 0;
        const layer = this._avatar.animLayer;
        if (layer && this._avatar.hasState('idle')) {
            try { layer.play('idle'); } catch { /* ignore */ }
        }
    }

    /** Hold the locomotion layer in idle without replaying it every frame. */
    forceIdle(): void {
        this._manualAction = null;
        this._airborneTime = 0;
        if (this._current === 'idle') return;
        const layer = this._avatar.animLayer;
        if (!layer || !this._avatar.hasState('idle')) {
            this._current = null;
            return;
        }
        try {
            layer.transition('idle', FADE_LOCO);
            this._current = 'idle';
        } catch (e) {
            console.warn('[AnimationStateMachine] forceIdle failed:', e);
        }
    }
}

export { AnimationStateMachine };
